import React from "react";
import { Link } from "react-router-dom";
import { Container, SectionLabel, TrustChip } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Search, ShieldCheck, Wallet, Users, CheckCircle2, Clock, ArrowRight } from "lucide-react";

const STEPS = [
  { icon: Search, t: "Create your fundraiser", d: "Add your title, story, beneficiary, category, and intended use of funds. It's free to start." },
  { icon: Users, t: "Complete onboarding", d: "Verify your legal name, email, and phone. Connect a U.S. payout account through Stripe." },
  { icon: ShieldCheck, t: "Get reviewed", d: "FightFund reviews your campaign. Verification evidence scales with category and risk before you go live." },
  { icon: Wallet, t: "Raise funds", d: "Donors give in seconds with guest checkout. Track supporters and post updates." },
  { icon: Clock, t: "Request a payout", d: "Request funds when ready. A 5% platform fee (3% for Pro) + Stripe processing fee applies. First payouts hold a minimum of 7 days after settlement and review." },
  { icon: CheckCircle2, t: "Controlled release", d: "Funds release only through Stripe rails once release conditions are met." },
];

export default function HowItWorks() {
  return (
    <div>
      <section className="border-b border-ff-border bg-ff-blue text-white">
        <Container className="py-16 lg:py-20">
          <SectionLabel><span className="text-white/60">How it works</span></SectionLabel>
          <h1 className="mt-3 max-w-3xl font-heading text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            Simple for donors. Accountable for everyone.
          </h1>
          <p className="mt-5 max-w-2xl text-white/70">
            FightFund reviews campaigns and payout requests before funds are released. Here's the full
            journey from starting a fundraiser to a controlled payout.
          </p>
        </Container>
      </section>

      <section className="bg-white">
        <Container className="py-16">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {STEPS.map((s, i) => (
              <div key={s.t} className="rounded-xl border border-ff-border bg-white p-8 shadow-sm">
                <div className="mb-4 flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-ff-blue text-white">
                    <s.icon className="h-5 w-5" />
                  </span>
                  <span className="font-heading text-3xl font-extrabold text-[#F3F4F6]">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="font-heading text-lg font-bold text-ff-blue">{s.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ff-dark">{s.d}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section className="border-t border-ff-border bg-[#F3F4F6]">
        <Container className="py-16">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
            <div>
              <SectionLabel>Public trust labels</SectionLabel>
              <h2 className="mt-2 font-heading text-2xl font-bold tracking-tight text-ff-blue sm:text-3xl">
                Know exactly where a campaign stands
              </h2>
              <p className="mt-4 max-w-lg text-ff-dark">
                We show honest, factual labels — never misleading claims. Labels reflect the real state of
                review, verification, and payout.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              {["Campaign Reviewed", "Organizer Verified", "Nonprofit Verified", "Payout Under Review", "Funds Released", "Reported / Under Review"]
                .map((l) => <TrustChip key={l} label={l} size="md" />)}
            </div>
          </div>
        </Container>
      </section>

      <section className="bg-white">
        <Container className="flex flex-col items-center gap-4 py-16 text-center">
          <h2 className="font-heading text-2xl font-bold text-ff-blue sm:text-3xl">Ready to get started?</h2>
          <Button asChild size="lg" className="bg-ff-action font-semibold text-white hover:bg-ff-actionDark">
            <Link to="/start-a-fundraiser" data-testid="hiw-start">Start a Fundraiser <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
          <p className="mt-1 text-sm text-ff-dark">
            Still have questions?{" "}
            <Link to="/faq" data-testid="hiw-faq-link" className="font-semibold text-ff-blue underline underline-offset-2 hover:text-ff-red">
              Read the FAQ
            </Link>
          </p>
        </Container>
      </section>
    </div>
  );
}
